<script setup lang="ts">
import { computed } from 'vue';
import { useQueries } from '@tanstack/vue-query';
import { useGetElectionTopOI } from '@/api/forecasttrader.queries';
import { eventContractBidAskQueryOptions } from '@/api/mdfarm.queries';
import { useUserState } from '@/stores/useUserState';
import BetSlip from './BetSlip.vue';
import BetSlipSkeleton from './BetSlipSkeleton.vue';
import BetSlipError from './BetSlipError.vue';
import { createBidPriceChips, getPartyColor } from './utils';
import type { BetSlipProps, BetSlipItem } from '@/types/betslip.types';

const props = defineProps<{
  productConid: number;
}>();

const emit = defineEmits<{
  'header-click': [];
  'item-click': [item: BetSlipItem];
}>();

const userState = useUserState();

// Fetch all contracts for product
const electionQuery = useGetElectionTopOI(
  computed(() => ({ productConid: props.productConid, sortBy: 'ask_price' as const })),
);

// Collect all conid+exchange pairs for batch price fetch
const allConidPairs = computed(() => {
  if (!electionQuery.data.value?.contracts) return [];
  return electionQuery.data.value.contracts.flatMap((c) => [
    { conid: c.conid_yes, exchange: c.exchange },
    { conid: c.conid_no, exchange: c.exchange },
  ]);
});

// Use useQueries for parallel price fetching (polls every 7s)
const priceQueries = useQueries({
  queries: computed(() =>
    allConidPairs.value.map((p) => eventContractBidAskQueryOptions(p.conid, p.exchange, userState.usePublicApi)),
  ),
});

// Aggregate prices into a lookup map (decimal format 0-1)
const priceMap = computed(() => {
  const map: Record<number, number | null | undefined> = {};
  priceQueries.value.forEach((query, index) => {
    const pair = allConidPairs.value[index];
    if (pair && query.data) {
      map[pair.conid] = query.data.bid;
    }
  });
  return map;
});

// Loading state - election data is primary
const isLoading = computed(() => electionQuery.isLoading.value);

// Error state
const isError = computed(() => electionQuery.isError.value);

// Ready state
const isReady = computed(() => !!electionQuery.data.value);

// Transform to BetSlipProps
const betSlipProps = computed<BetSlipProps | null>(() => {
  if (!electionQuery.data.value) return null;

  const election = electionQuery.data.value;

  return {
    title: election.market_name,
    items: election.contracts.map((contract) => ({
      id: contract.conid_yes,
      title: contract.strike_label,
      titleColor: getPartyColor(contract.party_affiliation),
      link: `/#/election-hub?eh_view=sharecontracts&eh_conid=${contract.conid_yes}`,
      // image: `/tws.proxy/public/icons/forecastx?conid=${contract.conid_yes}&fallback=true`,
      chips: createBidPriceChips(priceMap.value[contract.conid_yes], priceMap.value[contract.conid_no]),
    })),
  };
});

// Refetch all queries
function refetchAll() {
  electionQuery.refetch();
}

function handleHeaderClick() {
  emit('header-click');
}

function handleItemClick(item: BetSlipItem) {
  emit('item-click', item);
}

// Expose computed data for parent components (e.g., ShareBetSlipDialog)
defineExpose({
  betSlipProps,
  marketName: computed(() => electionQuery.data.value?.market_name || 'Election Contract'),
  betSlipLink: computed(() => `/#/election-hub?eh_view=sharecontracts&eh_product_conid=${props.productConid}`),
});
</script>

<template>
  <BetSlipSkeleton v-if="isLoading" :item-count="5" />
  <BetSlipError v-else-if="isError" @retry="refetchAll" />
  <BetSlip
    v-else-if="isReady && betSlipProps"
    v-bind="betSlipProps"
    @header-click="handleHeaderClick"
    @item-click="handleItemClick"
  />
</template>
